// Marketplace Constants and Configurations

import { MarketplaceCode, MarketplaceConfig } from '../types/transaction';

// Zone names for each marketplace
export const ZONE_NAMES: Record<MarketplaceCode, Record<string, string>> = {
  US: {
    '0': 'Northeast',
    '1': 'New York',
    '2': 'Mid-Atlantic',
    '3': 'Southeast',
    '4': 'Great Lakes',
    '5': 'Upper Midwest',
    '6': 'Central',
    '7': 'South Central',
    '8': 'Mountain West',
    '9': 'West Coast',
    'INT': 'International'
  },
  UK: {
    'A': 'Aberdeen',
    'B': 'Birmingham',
    'C': 'Cardiff/Cambridge',
    'D': 'Dundee',
    'E': 'East London',
    'F': 'Falkirk',
    'G': 'Glasgow',
    'H': 'Hull/Huddersfield',
    'I': 'Inverness/Ipswich',
    'J': 'Jersey',
    'K': 'Kent/Kingston',
    'L': 'Liverpool/Leeds',
    'M': 'Manchester',
    'N': 'North London',
    'O': 'Oxford',
    'P': 'Plymouth/Preston',
    'R': 'Reading',
    'S': 'Sheffield/South',
    'T': 'Taunton/Torquay',
    'U': 'Southall',
    'V': 'Coventry',
    'W': 'West London',
    'X': 'Kilmarnock',
    'Y': 'York',
    'Z': 'Lerwick',
    'INT': 'International'
  },
  DE: {
    '0': 'Berlin',
    '1': 'Hamburg',
    '2': 'Hannover',
    '3': 'Düsseldorf',
    '4': 'Frankfurt',
    '5': 'Kassel',
    '6': 'Nürnberg',
    '7': 'Stuttgart',
    '8': 'München',
    '9': 'Dresden',
    'INT': 'International'
  },
  FR: {
    '0': 'Île-de-France',
    '1': 'Paris Nord',
    '2': 'Picardie',
    '3': 'Normandie',
    '4': 'Loire',
    '5': 'Aquitaine',
    '6': 'Rhône-Alpes',
    '7': 'Provence',
    '8': 'Corse',
    '9': 'Occitanie',
    'INT': 'International'
  },
  IT: {
    '0': 'Roma',
    '1': 'Milano',
    '2': 'Varese',
    '3': 'Venezia',
    '4': 'Bologna',
    '5': 'Firenze',
    '6': 'Pescara',
    '7': 'Napoli',
    '8': 'Palermo',
    '9': 'Reggio',
    'INT': 'International'
  },
  ES: {
    '0': 'Madrid',
    '1': 'Barcelona',
    '2': 'Sevilla',
    '3': 'Valencia',
    '4': 'Cádiz',
    '5': 'Zaragoza',
    '6': 'Asturias',
    '7': 'Valladolid',
    '8': 'Bilbao',
    '9': 'Galicia',
    'INT': 'International'
  },
  CA: {
    'A': 'Newfoundland',
    'B': 'Nova Scotia',
    'C': 'PEI',
    'E': 'New Brunswick',
    'G': 'Quebec East',
    'H': 'Montreal',
    'J': 'Quebec West',
    'K': 'Eastern Ontario',
    'L': 'Central Ontario',
    'M': 'Toronto',
    'N': 'Southwest Ontario',
    'P': 'Northern Ontario',
    'R': 'Manitoba',
    'S': 'Saskatchewan',
    'T': 'Alberta',
    'V': 'BC Vancouver',
    'X': 'BC/NT/NU',
    'Y': 'Yukon',
    'INT': 'International'
  },
  AU: {},
  AE: {},
  SA: {}
};

// Fallback refund recovery rate for marketplaces without an explicit config.
// Single source of truth — keep the refund-loss math consistent with the
// per-marketplace rates in MARKETPLACE_CONFIGS below.
export const DEFAULT_REFUND_RECOVERY_RATE = 0.30;

// Marketplace Configurations
export const MARKETPLACE_CONFIGS: Record<MarketplaceCode, MarketplaceConfig> = {
  US: {
    code: 'US',
    name: 'United States',
    currency: 'USD',
    currencySymbol: '$',
    hasVAT: false,
    vatIncludedInPrice: false,
    refundRecoveryRate: 0.50,
    grossSalesFormula: (s, _v) => s,
    hasLiquidations: false,
    hasPostalZones: true,
  },
  UK: {
    code: 'UK',
    name: 'United Kingdom',
    currency: 'GBP',
    currencySymbol: '£',
    hasVAT: true,
    vatIncludedInPrice: true,
    refundRecoveryRate: 0.30,
    grossSalesFormula: (s, v) => s + v,
    hasLiquidations: true,
    hasPostalZones: true,
  },
  DE: {
    code: 'DE',
    name: 'Germany',
    currency: 'EUR',
    currencySymbol: '€',
    hasVAT: true,
    vatIncludedInPrice: true,
    refundRecoveryRate: 0.30,
    grossSalesFormula: (s, v) => s + v,
    hasLiquidations: true,
    hasPostalZones: true,
  },
  FR: {
    code: 'FR',
    name: 'France',
    currency: 'EUR',
    currencySymbol: '€',
    hasVAT: true,
    vatIncludedInPrice: true,
    refundRecoveryRate: 0.30,
    grossSalesFormula: (s, v) => s + v,
    hasLiquidations: false,
    hasPostalZones: true,
  },
  IT: {
    code: 'IT',
    name: 'Italy',
    currency: 'EUR',
    currencySymbol: '€',
    hasVAT: true,
    vatIncludedInPrice: true,
    refundRecoveryRate: 0.30,
    grossSalesFormula: (s, v) => s + v,
    hasLiquidations: false,
    hasPostalZones: true,
  },
  ES: {
    code: 'ES',
    name: 'Spain',
    currency: 'EUR',
    currencySymbol: '€',
    hasVAT: true,
    vatIncludedInPrice: true,
    refundRecoveryRate: 0.30,
    grossSalesFormula: (s, v) => s + v,
    hasLiquidations: false,
    hasPostalZones: true,
  },
  AU: {
    code: 'AU',
    name: 'Australia',
    currency: 'AUD',
    currencySymbol: 'A$',
    hasVAT: false,  // FIXED: AU uses GST but it's not tracked separately in transaction reports
    vatIncludedInPrice: false,
    refundRecoveryRate: 0.40,
    grossSalesFormula: (s, _v) => s,
    hasLiquidations: false,
    hasPostalZones: false,
  },
  CA: {
    code: 'CA',
    name: 'Canada',
    currency: 'CAD',
    currencySymbol: 'C$',
    hasVAT: false,  // FIXED: CA uses GST/HST but it's not tracked separately in transaction reports (like AU)
    vatIncludedInPrice: false,
    refundRecoveryRate: 0.40,
    grossSalesFormula: (s, _v) => s,
    hasLiquidations: false,
    hasPostalZones: true,
  },
  AE: {
    code: 'AE',
    name: 'UAE',
    currency: 'AED',
    currencySymbol: 'د.إ',
    hasVAT: false,
    vatIncludedInPrice: false,
    refundRecoveryRate: 0.30,
    grossSalesFormula: (s, _v) => s,
    hasLiquidations: false,
    hasPostalZones: false,
  },
  SA: {
    code: 'SA',
    name: 'Saudi Arabia',
    currency: 'SAR',
    currencySymbol: '﷼',
    hasVAT: false,
    vatIncludedInPrice: false,
    refundRecoveryRate: 0.30,
    grossSalesFormula: (s, _v) => s,
    hasLiquidations: false,
    hasPostalZones: false,
  },
};

// All marketplace codes
export const ALL_MARKETPLACES: MarketplaceCode[] = ['US', 'UK', 'DE', 'FR', 'IT', 'ES', 'CA', 'AU', 'AE', 'SA'];

// Get config for a marketplace
export const getMarketplaceConfig = (code: MarketplaceCode): MarketplaceConfig => {
  return MARKETPLACE_CONFIGS[code];
};

// Get zone name for a marketplace and zone code
export const getZoneName = (marketplace: MarketplaceCode, zoneCode: string): string => {
  return ZONE_NAMES[marketplace]?.[zoneCode] || zoneCode;
};
